import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { Segment } from '@mi-front/core';
import { Milliseconds, toDateTimeStr } from '@mi-front/core';
import { useCallback } from 'react';
import { useStreamSegmentsList } from '../../hooks/segments/useStreamSegmentsQuery';
import { useStreamSettingsStore } from '../../stores/stream-screen-settings.store';
import { useStreamSelection } from '../../stores/useStreamSelection';
import { StreamMode } from '../../types';
import { ChunkInput, useFragmentEditorStore } from '../stores/useFragmentEditorStore';

/**
 * Hook that encapsulates the logic for adding a chunk from the current selection.
 * Replaces the logic from the addChunkFromFocusSelectionEpic.
 */
export function useAddChunkFromSelection() {
  const station = useActiveStation();
  const mode = useStreamSettingsStore((state) => state.mode);
  const segments = useStreamSegmentsList();

  const { addChunk, canAddChunk } = useFragmentEditorStore();
  const { selectionExists, selectionRange } = useStreamSelection();

  const addChunkFromSelection = useCallback(() => {
    // Only allow adding if we are in editor mode
    if (mode !== StreamMode.EDITOR) {
      return;
    }

    // Verify that a valid selection exists
    if (!selectionExists) {
      return;
    }

    // Filter segments that overlap with the selection
    const filteredSegments: readonly Segment[] = segments.filter(
      (s) => selectionRange.from <= s.endTs && selectionRange.to >= s.startTs,
    );

    const chunk: ChunkInput = {
      label: undefined,
      duration: (selectionRange.to - selectionRange.from) as Milliseconds,
      from: toDateTimeStr(selectionRange.from),
      to: toDateTimeStr(selectionRange.to),
      segments: filteredSegments,
      highlights: [],
    };

    addChunk(chunk, station.id);
  }, [mode, segments, station.id, addChunk, selectionExists, selectionRange]);

  const isEnabled = canAddChunk(station.id) && mode === StreamMode.EDITOR;

  return {
    addChunkFromSelection,
    canAddChunk: isEnabled,
  };
}
